<template>
    <div
        class="pt-picker-panel pt-date-range-picker"
        :class="[
            {
                'has-sidebar': $slots.sidebar || hasShortcuts,
                'has-time': false,
            },
        ]"
    >
        <div class="pt-picker-panel__body-wrapper">
            <slot name="sidebar" class="pt-picker-panel__sidebar"></slot>
            <div v-if="hasShortcuts" class="pt-picker-panel__sidebar">
                <div v-for="(shortcut, key) in shortcuts" :key="key" class="pt-picker-panel__shortarea">
                    <pt-popover
                        :append-to-body="false"
                        v-if="shortcut.type === 'custom_day'"
                        placement="right"
                        :width="160"
                        trigger="hover"
                    >
                        <template #reference>
                            <div class="pt-picker-panel__more">
                                <pt-icon
                                    class="pt-shortcut-select"
                                    :icon="currentShortcut === shortcut.type ? 'pt-icon-tick' : ''"
                                ></pt-icon>
                                <pt-button
                                    color="dark"
                                    :class="[
                                        {
                                            'pt-shortcut-more-active': currentShortcut === shortcut.type,
                                        },
                                    ]"
                                    class="pt-picker-panel__shortcut"
                                    @click="handleShortcutClick(shortcut)"
                                    type="text"
                                >
                                    {{
                                        t('pt.datepicker.last_days', {
                                            day: customDate,
                                        })
                                    }}</pt-button
                                >
                                <pt-icon class="pt-picker-panel__more-icon" icon="pt-icon-arrow-16"></pt-icon>
                            </div>
                        </template>
                        <template #default>
                            <div>
                                <span class="pt-picker-panel__custom-day-text">{{ t('pt.datepicker.last') }}</span>
                                <pt-input-number
                                    class="pt-picker-panel__custom-day"
                                    :min="shortcut.range.min || 0"
                                    :max="shortcut.range.max || 30"
                                    :step="shortcut.range.step || 1"
                                    v-model="customDate"
                                    @change="() => handleCustomDay(shortcut)"
                                />
                                <span class="pt-picker-panel__custom-day-text">{{ t('pt.datepicker.days') }}</span>
                                <pt-checkbox
                                    class="pt-picker-panel__custom-day-checkbox"
                                    @change="() => handleCustomDay(shortcut)"
                                    v-model="includeToday"
                                    >{{ t('pt.datepicker.include_today') }}</pt-checkbox
                                >
                            </div>
                        </template>
                    </pt-popover>
                    <div v-else style="position: relative">
                        <pt-icon
                            class="pt-shortcut-select"
                            :icon="currentShortcut === shortcut.type ? 'pt-icon-tick' : ''"
                        ></pt-icon>
                        <pt-button
                            color="dark"
                            class="pt-picker-panel__shortcut"
                            :class="[
                                {
                                    'pt-shortcut-more-active': currentShortcut === shortcut.type,
                                },
                            ]"
                            @click="handleShortcutClick(shortcut)"
                            type="text"
                        >
                            {{ shortcut.text }}</pt-button
                        >
                    </div>
                </div>
            </div>
            <div class="pt-picker-panel__body">
                <div class="pt-picker-panel__time-header">
                    <span>{{ currentShortcutText }}</span>
                    <span v-if="minVisibleTime"> ({{ minVisibleTime }} - {{ maxVisibleTime }}) </span>
                </div>
                <div class="pt-picker-panel__content pt-date-range-picker__content is-left">
                    <div class="pt-date-range-picker__header">
                        <button
                            type="button"
                            class="pt-picker-panel__icon-btn pt-icon-d-arrow-left"
                            @click="leftPrevYear"
                        >
                            <pt-icon icon="pt-icon--unfold"></pt-icon>
                        </button>
                        <button
                            type="button"
                            class="pt-picker-panel__icon-btn pt-icon-arrow-left"
                            @click="leftPrevMonth"
                        >
                            <pt-icon icon="pt-icon--back"></pt-icon>
                        </button>
                        <button
                            v-if="unlinkPanels"
                            type="button"
                            :disabled="!enableYearArrow"
                            :class="{ 'is-disabled': !enableYearArrow }"
                            class="pt-picker-panel__icon-btn pt-icon-d-arrow-right"
                            @click="leftNextYear"
                        >
                            <pt-icon icon="pt-icon--fold"></pt-icon>
                        </button>
                        <button
                            v-if="unlinkPanels"
                            type="button"
                            :disabled="!enableMonthArrow"
                            :class="{ 'is-disabled': !enableMonthArrow }"
                            class="pt-picker-panel__icon-btn pt-icon-arrow-right"
                            @click="leftNextMonth"
                        >
                            <pt-icon icon="pt-icon--more"></pt-icon>
                        </button>
                        <div>{{ leftLabel }}</div>
                    </div>
                    <date-table
                        selection-mode="range"
                        :date="leftDate"
                        :min-date="minDate"
                        :max-date="maxDate"
                        :range-state="rangeState"
                        :timeZone="timeZone"
                        :disabled-date="disabledDate"
                        :cell-class-name="cellClassName"
                        @changerange="handleChangeRange"
                        @pick="handleRangePick"
                        @select="onSelect"
                    />
                </div>
                <div class="pt-picker-panel__content pt-date-range-picker__content is-right">
                    <div class="pt-date-range-picker__header">
                        <button
                            v-if="unlinkPanels"
                            type="button"
                            :disabled="!enableYearArrow"
                            :class="{ 'is-disabled': !enableYearArrow }"
                            class="pt-picker-panel__icon-btn pt-icon-d-arrow-left"
                            @click="rightPrevYear"
                        >
                            <pt-icon icon="pt-icon--unfold"></pt-icon>
                        </button>
                        <button
                            v-if="unlinkPanels"
                            type="button"
                            :disabled="!enableMonthArrow"
                            :class="{ 'is-disabled': !enableMonthArrow }"
                            class="pt-picker-panel__icon-btn pt-icon-arrow-left"
                            @click="rightPrevMonth"
                        >
                            <pt-icon icon="pt-icon--back"></pt-icon>
                        </button>
                        <button
                            type="button"
                            class="pt-picker-panel__icon-btn pt-icon-arrow-right"
                            @click="rightNextYear"
                        >
                            <pt-icon icon="pt-icon--fold"></pt-icon>
                        </button>
                        <button
                            type="button"
                            class="pt-picker-panel__icon-btn pt-icon-d-arrow-right"
                            @click="rightNextMonth"
                        >
                            <pt-icon icon="pt-icon--more"></pt-icon>
                        </button>

                        <div>{{ rightLabel }}</div>
                    </div>
                    <date-table
                        selection-mode="range"
                        :date="rightDate"
                        :min-date="minDate"
                        :max-date="maxDate"
                        :range-state="rangeState"
                        :disabled-date="disabledDate"
                        :cell-class-name="cellClassName"
                        :timeZone="timeZone"
                        @changerange="handleChangeRange"
                        @pick="handleRangePick"
                        @select="onSelect"
                    />
                </div>
            </div>
        </div>
        <div class="pt-picker-panel__option-footer">
            <pt-button
                v-if="clearable"
                size="mini"
                color="grey"
                type="outline"
                class="pt-picker-panel__link-btn pt-picker-panel__link-cancel"
                @click="handleClear"
            >
                {{ t('pt.datepicker.cancel') }}
            </pt-button>
            <pt-button size="mini" class="pt-picker-panel__link-btn" @click="handleConfirm(false)">
                {{ t('pt.datepicker.apply') }}
            </pt-button>
        </div>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, inject, PropType, ref, watch } from 'vue';
import { t } from '../../../locale';
import { extractDateFormat, extractTimeFormat, TimePickPanel } from '../../../time-picker';

import { ClickOutside } from '../../../directives';
import { isValidDatePickType } from '../../../../utils/validators';
import dayjs, { Dayjs } from 'dayjs';
import DateTable from './basic-date-table.vue';
import PtInput from '../../../input';
import PtButton from '../../../button';
import PtPopper from '../../../popper';

import type { IDatePickerType } from '../date-picker.type';

export default defineComponent({
    directives: { clickoutside: ClickOutside },

    components: { TimePickPanel, DateTable, PtInput, PtButton, PtPopper },

    props: {
        unlinkPanels: Boolean,
        visible: Boolean,
        defaultShortcut: {
            type: Object,
        },
        timeZone: String,
        parsedValue: {
            type: Array as PropType<Dayjs[]>,
        },
        type: {
            type: String as PropType<IDatePickerType>,
            required: true,
            validator: isValidDatePickType,
        },
    },

    emits: ['pick', 'close', 'set-picker-option'],

    setup(props, ctx) {
        const leftDate = ref(dayjs());
        const rightDate = ref(dayjs().add(1, 'month'));
        const minDate = ref();
        const maxDate = ref();
        const customDate = ref('7');
        const includeToday = ref(true);
        const dateUserInput = ref({
            min: null,
            max: null,
        });

        const timeUserInput = ref({
            min: null,
            max: null,
        });

        const leftLabel = computed(() => {
            return (
                leftDate.value.year() +
                ' ' +
                t('pt.datepicker.year') +
                ' ' +
                t(`pt.datepicker.month${leftDate.value.month() + 1}`)
            );
        });

        const rightLabel = computed(() => {
            return (
                rightDate.value.year() +
                ' ' +
                t('pt.datepicker.year') +
                ' ' +
                t(`pt.datepicker.month${rightDate.value.month() + 1}`)
            );
        });

        const leftYear = computed(() => {
            return leftDate.value.year();
        });

        const leftMonth = computed(() => {
            return leftDate.value.month();
        });

        const rightYear = computed(() => {
            return rightDate.value.year();
        });

        const rightMonth = computed(() => {
            return rightDate.value.month();
        });

        const hasShortcuts = computed(() => !!shortcuts.length);

        const minVisibleDate = computed(() => {
            if (dateUserInput.value.min !== null) return dateUserInput.value.min;
            if (minDate.value) return minDate.value.format(dateFormat.value);
            return '';
        });

        const maxVisibleDate = computed(() => {
            if (dateUserInput.value.max !== null) return dateUserInput.value.max;
            if (maxDate.value || minDate.value) return (maxDate.value || minDate.value).format(dateFormat.value);
            return '';
        });

        const minVisibleTime = computed(() => {
            if (timeUserInput.value.min !== null) return timeUserInput.value.min;
            if (minDate.value) return minDate.value.format(timeFormat.value).replace(/T[\s\S]*/, '');
            return '';
        });

        const maxVisibleTime = computed(() => {
            if (timeUserInput.value.max !== null) return timeUserInput.value.max;
            if (maxDate.value || minDate.value)
                return (maxDate.value || minDate.value).format(timeFormat.value).replace(/T[\s\S]*/, '');
            return '';
        });

        const timeFormat = computed(() => {
            return extractTimeFormat(format);
        });

        const dateFormat = computed(() => {
            return extractDateFormat(format);
        });

        const leftPrevYear = () => {
            leftDate.value = leftDate.value.subtract(1, 'year');
            if (!props.unlinkPanels) {
                rightDate.value = leftDate.value.add(1, 'month');
            }
        };

        const leftPrevMonth = () => {
            leftDate.value = leftDate.value.subtract(1, 'month');
            if (!props.unlinkPanels) {
                rightDate.value = leftDate.value.add(1, 'month');
            }
        };

        const rightNextYear = () => {
            if (!props.unlinkPanels) {
                leftDate.value = leftDate.value.add(1, 'year');
                rightDate.value = leftDate.value.add(1, 'month');
            } else {
                rightDate.value = rightDate.value.add(1, 'year');
            }
        };

        const rightNextMonth = () => {
            if (!props.unlinkPanels) {
                leftDate.value = leftDate.value.add(1, 'month');
                rightDate.value = leftDate.value.add(1, 'month');
            } else {
                rightDate.value = rightDate.value.add(1, 'month');
            }
        };

        const leftNextYear = () => {
            leftDate.value = leftDate.value.add(1, 'year');
        };

        const leftNextMonth = () => {
            leftDate.value = leftDate.value.add(1, 'month');
        };

        const rightPrevYear = () => {
            rightDate.value = rightDate.value.subtract(1, 'year');
        };

        const rightPrevMonth = () => {
            rightDate.value = rightDate.value.subtract(1, 'month');
        };

        const currentShortcutText = computed(() => {
            if (!currentShortcut.value) return '';
            const _shortcut = shortcuts.find((shortcut) => shortcut.type === currentShortcut.value);
            if (currentShortcut.value === 'custom_day') {
                return t('pt.datepicker.last_days', {
                    day: customDate.value,
                });
            }
            return _shortcut ? _shortcut.text : '';
        });

        const enableMonthArrow = computed(() => {
            const nextMonth = (leftMonth.value + 1) % 12;
            const yearOffset = leftMonth.value + 1 >= 12 ? 1 : 0;
            return (
                props.unlinkPanels &&
                new Date(leftYear.value + yearOffset, nextMonth) < new Date(rightYear.value, rightMonth.value)
            );
        });

        const enableYearArrow = computed(() => {
            return (
                props.unlinkPanels &&
                rightYear.value * 12 + rightMonth.value - (leftYear.value * 12 + leftMonth.value + 1) >= 12
            );
        });

        const isValidValue = (value) => {
            return Array.isArray(value) && value[0] && value[1] && value[0].valueOf() <= value[1].valueOf();
        };

        const rangeState = ref({
            endDate: null,
            selecting: false,
        });

        const handleChangeRange = (val) => {
            rangeState.value = val;
        };

        const onSelect = (selecting) => {
            rangeState.value.selecting = selecting;
            if (!selecting) {
                rangeState.value.endDate = null;
            }
        };

        const handleConfirm = (visible = false) => {
            confirmShortcut.value = currentShortcut.value;
            if (isValidValue([minDate.value, maxDate.value])) {
                ctx.emit('pick', [minDate.value, maxDate.value], visible);
            }
        };

        const formatEmit = (emitDayjs: Dayjs, index?) => {
            if (!emitDayjs) return;
            if (defaultTime) {
                const defaultTimeD = dayjs(defaultTime[index] || defaultTime);
                return defaultTimeD.year(emitDayjs.year()).month(emitDayjs.month()).date(emitDayjs.date());
            }
            return emitDayjs;
        };

        const handleRangePick = (val, close = true) => {
            if (val.minDate && !val.maxDate) {
                val.maxDate = val.minDate;
            }
            const hasCustom = shortcuts.findIndex((shortcut: any) => shortcut.type === 'custom');
            if (hasCustom > -1) {
                currentShortcut.value = shortcuts[hasCustom].type;
            }

            const minDate_ = formatEmit(val.minDate, 0);
            const maxDate_ = formatEmit(val.maxDate, 1);

            if (maxDate.value === maxDate_ && minDate.value === minDate_) {
                return;
            }
            maxDate.value = maxDate_;
            minDate.value = minDate_;
        };

        const handleShortcutClick = (shortcut: any) => {
            currentShortcut.value = shortcut.type;
            if (shortcut.type === 'custom') return;
            if (shortcut.type === 'custom_day') {
                handleCustomDay(shortcut);
                return;
            }
            minDate.value = dayjs(shortcut.value[0]);
            maxDate.value = dayjs(shortcut.value[1]);
            updateDate([dayjs(shortcut.value[0]), dayjs(shortcut.value[1])]);
            return;
        };

        const handleCustomDay = (shortcut: any) => {
            currentShortcut.value = shortcut.type;
            const time = shortcut['calcFunc']({
                isIncludeToday: includeToday.value,
                value: customDate.value,
            });
            minDate.value = dayjs(time[0]);
            maxDate.value = dayjs(time[1]);
            updateDate([dayjs(time[0]), dayjs(time[1])]);
            return;
        };

        const minTimePickerVisible = ref(false);
        const maxTimePickerVisible = ref(false);

        const handleMinTimeClose = () => {
            minTimePickerVisible.value = false;
        };

        const handleMaxTimeClose = () => {
            maxTimePickerVisible.value = false;
        };

        const handleDateChange = (value, type) => {
            dateUserInput.value[type] = null;
        };

        const handleClear = () => {
            ctx.emit('close');
        };

        const formatToString = (value) => {
            return Array.isArray(value) ? value.map((_) => _.format(format)) : value.format(format);
        };

        const parseUserInput = (value) => {
            return Array.isArray(value) ? value.map((_) => dayjs(_, format)) : dayjs(value, format);
        };

        const getDefaultValue = () => {
            let start;
            if (Array.isArray(defaultValue)) {
                const left = dayjs(defaultValue[0]);
                let right = dayjs(defaultValue[1]);
                if (!props.unlinkPanels) {
                    right = left.add(1, 'month');
                }
                return [left, right];
            } else if (defaultValue) {
                start = dayjs(defaultValue);
            } else {
                start = dayjs();
            }
            return [start, start.add(1, 'month')];
        };

        ctx.emit('set-picker-option', ['isValidValue', isValidValue]);
        ctx.emit('set-picker-option', ['parseUserInput', parseUserInput]);
        ctx.emit('set-picker-option', ['formatToString', formatToString]);
        ctx.emit('set-picker-option', ['handleClear', handleClear]);

        const pickerBase = inject('EP_PICKER_BASE') as any;
        const { shortcuts, disabledDate, cellClassName, format, defaultTime, defaultValue, arrowControl, clearable } =
            pickerBase.props;

        const currentShortcut = ref(shortcuts.length ? 'custom' : '');
        const confirmShortcut = ref('custom');

        if (props.defaultShortcut) {
            if (props.defaultShortcut.type === 'custom_day') {
                includeToday.value = props.defaultShortcut.includeToday;
                customDate.value = props.defaultShortcut.number;
            }
            currentShortcut.value = props.defaultShortcut.type;
            confirmShortcut.value = props.defaultShortcut.type;
        }

        const updateDate = (newVal: any) => {
            if (newVal && newVal.length === 2) {
                minDate.value = newVal[0];
                maxDate.value = newVal[1];
                leftDate.value = minDate.value;
                if (props.unlinkPanels && maxDate.value) {
                    const minDateYear = minDate.value.year();
                    const minDateMonth = minDate.value.month();
                    const maxDateYear = maxDate.value.year();
                    const maxDateMonth = maxDate.value.month();
                    rightDate.value =
                        minDateYear === maxDateYear && minDateMonth === maxDateMonth
                            ? maxDate.value.add(1, 'month')
                            : maxDate.value;
                } else {
                    rightDate.value = leftDate.value.add(1, 'month');
                }
            } else {
                const defaultArr = getDefaultValue();
                minDate.value = null;
                maxDate.value = null;
                leftDate.value = defaultArr[0];
                rightDate.value = defaultArr[1];
            }
        };

        watch(
            () => props.visible,
            (newVal) => {
                if (newVal) {
                    currentShortcut.value = confirmShortcut.value;
                    updateDate(props.parsedValue);
                }
            },
        );

        watch(
            () => props.parsedValue,
            (newVal) => {
                updateDate(newVal);
            },
            { immediate: true },
        );

        return {
            shortcuts,
            disabledDate,
            cellClassName,
            minTimePickerVisible,
            maxTimePickerVisible,
            handleMinTimeClose,
            handleMaxTimeClose,
            handleShortcutClick,
            rangeState,
            minDate,
            maxDate,
            customDate,
            handleRangePick,
            onSelect,
            handleChangeRange,
            enableYearArrow,
            enableMonthArrow,
            rightPrevMonth,
            rightPrevYear,
            rightNextMonth,
            rightNextYear,
            leftPrevMonth,
            leftPrevYear,
            leftNextMonth,
            leftNextYear,
            hasShortcuts,
            leftLabel,
            rightLabel,
            leftDate,
            rightDate,
            t,
            minVisibleDate,
            maxVisibleDate,
            minVisibleTime,
            maxVisibleTime,
            arrowControl,
            handleDateChange,
            handleClear,
            handleConfirm,
            timeFormat,
            clearable,
            currentShortcut,
            handleCustomDay,
            includeToday,
            currentShortcutText,
        };
    },
});
</script>
